import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigation } from 'lucide-react';
import { NavTarget, googleMapsUrl, wazeUrl } from '../../utils/geo';

interface NavigateButtonProps {
  target: NavTarget | null;
  /**
   * 'full': Navigate button + small Waze button (detail pages).
   * 'icon': round icon button, Google Maps only (listing cards).
   * 'compact': small text links (map cards).
   */
  variant?: 'full' | 'icon' | 'compact';
  /** Place name, for screen readers on the icon button. */
  name?: string;
  className?: string;
}

/** Navigate opens Google Maps by default, with a small Waze option (plan 7). */
export default function NavigateButton({ target, variant = 'full', name, className = '' }: NavigateButtonProps) {
  const { t } = useTranslation();
  if (!target) return null;
  const external = { target: '_blank', rel: 'noopener noreferrer' };

  if (variant === 'icon') {
    return (
      <a
        href={googleMapsUrl(target)}
        {...external}
        className={`shrink-0 flex items-center justify-center gap-1.5 rounded-full min-w-11 min-h-11 px-3 text-sm font-medium bg-fog text-ink hover:bg-ink/10 transition-colors ${className}`}
        aria-label={name ? `${t('directory.action.navigate')} – ${name}` : t('directory.action.navigate')}
      >
        <Navigation className="w-4 h-4" aria-hidden />
        <span className="hidden sm:inline">{t('directory.action.navigate')}</span>
      </a>
    );
  }

  if (variant === 'compact') {
    return (
      <span className={`inline-flex items-center gap-2 ${className}`}>
        <a
          href={googleMapsUrl(target)}
          {...external}
          className="inline-flex items-center gap-1 text-xs font-medium bg-ink !text-paper rounded-full px-3 py-1.5 hover:opacity-90"
        >
          <Navigation className="w-3 h-3" aria-hidden />
          {t('directory.action.navigate')}
        </a>
        <a
          href={wazeUrl(target)}
          {...external}
          className="text-xs font-medium !text-ink underline"
          aria-label={t('directory.action.openInWaze')}
        >
          {t('directory.action.waze')}
        </a>
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <a
        href={googleMapsUrl(target)}
        {...external}
        className="inline-flex items-center justify-center gap-2 rounded-full min-h-11 px-4 text-sm font-medium transition-colors bg-fog text-ink hover:bg-ink/10"
      >
        <Navigation className="w-4 h-4" aria-hidden />
        {t('directory.action.navigate')}
      </a>
      <a
        href={wazeUrl(target)}
        {...external}
        className="inline-flex items-center justify-center rounded-full min-h-11 px-3 text-xs font-medium text-ink border border-ink/15 hover:bg-fog transition-colors"
        aria-label={t('directory.action.openInWaze')}
      >
        {t('directory.action.waze')}
      </a>
    </span>
  );
}
